import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector, getClientDomainPrefix, getUser } from '../../DynamoConnector';
import { ENV } from '../../../config/featureFlags';

// DEV WORKAROUND — TODO(remove): force one specific Cognito sub to be treated as
// an active, paid Patreon supporter so subscriber-gated features (ad-free, perks)
// can be tested without a real subscription. Non-prod only; no effect on any other
// account or in production. Delete this and the applyDevPatreonOverride() call once
// real subscriber testing is set up.
const DEV_PATREON_OVERRIDE_SUB = 'f498f418-7061-707d-d071-acfa2afd733c';
const applyDevPatreonOverride = (results) => {
    if (ENV.isProd) return;
    if (!results || getUser()?.profile?.sub !== DEV_PATREON_OVERRIDE_SUB) return;
    results.subscriberFeatures = {
        ...results.subscriberFeatures,
        isMember: true,
        isSubscribed: true,
        subscriberTier: parseInt(results.subscriberFeatures?.subscriberTier, 10) || 1,
        subscriberTierName: results.subscriberFeatures?.subscriberTierName || 'Supporter',
    };
};

export const profileSlice = createSlice({
    name: 'profile',
    initialState: {
        metadata: {
            avatarUri: '',
            bio: '',
            id: null,
            profileName: '',
            isDiscordMod: false,
        },
        preferences: {
            importPreferences: {
                allowPublicImport: false,
                hideAuthorInfo: false,
                markDecksAsPrivate: false,
            },
            profilePreferences: {
                isPrivate: false,
                hideLinkedAccounts: false,
            },
            subscriberFeatures: {
                patreonLink: {
                    isLinked: false,
                },
                perks: {
                    deckRowHighlighting: '',
                    useDeckRowHighlighting: false,
                    showSubscriberLevelOnDeckRows: true,
                    showSubscriberLevelOnProfile: true,
                },
                isMember: false,
                subscriberTierName: null,
                isSubscribed: false,
                subscriberTier: 0,
            },
        },
        accountLink: {
            isFetchingIdentifierString: false,
            identifierString: '',
            isValidating: false,
            validationSuccessful: false,
            isDeleting: false,
        },
        manageDecks: {
            decks: [],
        },
        isFetching: false,
        profiles: {},
    },
    reducers: {
        setProfileData: (state, action) => {
            state.metadata = action.payload.metadata;
            state.profiles = action.payload.profiles;
            state.preferences.importPreferences = { ...action.payload.importPreferences };
            state.preferences.profilePreferences = { ...action.payload.profilePreferences };
            state.preferences.subscriberFeatures = { ...action.payload.subscriberFeatures };
        },
        setIsFetchingIdentifierString: (state, action) => {
            state.accountLink.isFetchingIdentifierString = action.payload;
        },
        setIdentifierString: (state, action) => {
            state.accountLink.identifierString = action.payload;
        },
        setIsValidating: (state, action) => {
            state.accountLink.isValidating = action.payload;
        },
        setIsDeleting: (state, action) => {
            state.accountLink.isDeleting = action.payload;
        },
        setValidationSuccessful: (state, action) => {
            state.accountLink.validationSuccessful = action.payload;
        },
        resetAddProfileDialog: (state, action) => {
            state.accountLink = {
                isFetchingIdentifierString: false,
                identifierString: '',
                isValidating: false,
                validationSuccessful: false,
                isDeleting: false,
            };
        },
        setIsFetching: (state, action) => {
            state.isFetching = action.payload;
        },
        setManageDecksList: (state, action) => {
            state.manageDecks.decks = action.payload.sort((a, b) => {
                if (a.title.toLowerCase() < b.title.toLowerCase()) {
                    return -1;
                } else {
                    return 1;
                }
            });
        },
        resetProfile: (state, action) => {
            state.metadata = {
                avatarUri: '',
                bio: '',
                id: null,
                profileName: '',
                isDiscordMod: false,
            };
            state.preferences = {
                importPreferences: {
                    allowPublicImport: false,
                    hideAuthorInfo: false,
                    markDecksAsPrivate: false,
                },
                profilePreferences: {
                    isPrivate: false,
                    hideLinkedAccounts: false,
                },
                subscriberFeatures: {
                    patreonLink: {
                        isLinked: false,
                    },
                    perks: {
                        deckRowHighlighting: '',
                        useDeckRowHighlighting: false,
                        showSubscriberLevelOnDeckRows: true,
                        showSubscriberLevelOnProfile: true,
                    },
                    isMember: false,
                    subscriberTierName: null,
                    isSubscribed: false,
                    subscriberTier: 0,
                },
            };
            state.manageDecks = {
                decks: [],
            };
            state.profiles = {};
        },
    },
});

// Action creators are generated for each case reducer function
export const {
    setProfileData,
    setIsFetchingIdentifierString,
    setIdentifierString,
    setIsValidating,
    setValidationSuccessful,
    setIsDeleting,
    resetAddProfileDialog,
    setManageDecksList,
    setIsFetching,
    resetProfile,
} = profileSlice.actions;

export const updateUserSettings = (settings) => (dispatch) => {
    dispatch(setIsFetching(true));
    DynamoConnector.postUpdateUserPreferences(settings, (results) => {
        dispatch(getUserProfile());
    });
};

export const uploadAvatar = (file) => (dispatch) => {
    dispatch(setIsFetching(true));
    DynamoConnector.uploadAvatar(file, () => {
        dispatch(getUserProfile());
    });
};

export const disconnectPatreon = () => (dispatch) => {
    dispatch(setIsFetching(true));
    DynamoConnector.deletePatreonLink((results) => {
        dispatch(getUserProfile());
    });
};

export const refreshPatreon = () => (dispatch) => {
    dispatch(setIsFetching(true));
    DynamoConnector.getRefreshPatreonMemberships((results) => {
        dispatch(getUserProfile());
    });
};

export const finalizePatreonCode = (code) => (dispatch) => {
    DynamoConnector.validatePatreonCode(code, (results) => {
        window.location.href = `${getClientDomainPrefix()}/settings`;
    });
};

export const getUserProfile = () => (dispatch) => {
    dispatch(setIsFetching(true));
    dispatch(resetProfile());
    DynamoConnector.getUserProfile((results) => {
        if (results) {
            applyDevPatreonOverride(results);
            dispatch(setProfileData(results));
            dispatch(setIsFetching(false));
        }
    });
};

export const postManageDecksListOperation =
    (operation, deckIds = []) =>
    (dispatch) => {
        dispatch(setIsFetching(true));
        DynamoConnector.postManagedDecksOp(operation, deckIds, (results) => {
            if (results) {
                dispatch(setManageDecksList(results));
            }

            dispatch(getManageDecksList());
        });
    };

export const getManageDecksList = () => (dispatch) => {
    dispatch(setIsFetching(true));
    DynamoConnector.getManagedDecks((results) => {
        if (results) {
            dispatch(setManageDecksList(results));
        }

        dispatch(setIsFetching(false));
    });
};

export const getAccountLinkCode = (provider) => (dispatch) => {
    dispatch(setIsFetchingIdentifierString(true));
    dispatch(setIdentifierString(null));

    DynamoConnector.getAccountLinkCode(provider, (results) => {
        if (results) {
            dispatch(setIdentifierString(results));
        }

        dispatch(setIsFetchingIdentifierString(false));
    });
};

export const postValidateAccountLink = (provider, username) => (dispatch) => {
    dispatch(setIsValidating(true));
    DynamoConnector.postValidateLinkCode(provider, username, (results) => {
        dispatch(setValidationSuccessful(results));
        dispatch(setIsValidating(false));
        dispatch(getUserProfile());
    });
};

export const deleteAccountLink = (list) => (dispatch) => {
    dispatch(setIsDeleting(true));
    dispatch(setIsFetching(true));
    DynamoConnector.deleteAccountLink(list, (results) => {
        dispatch(setIsDeleting(false));
        dispatch(getUserProfile());
    });
};

export default profileSlice.reducer;
